import React from 'react';
import { Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { Button } from 'components/index';
import SelectDivision from '../../glossary/SelectDivision';
import SelectInput from '../../SelectInput';
import { LineItemUnit, LineItemUnitEnum, LineItemLabelEnum, LineItemLabel } from '../../glossary/glossary.type';
import { Close } from '@mui/icons-material';

type ChangeOrderAction = 'no-change' | 'mark' | 'unmark';

interface BulkEditDrawLineItemsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: { divisionId: string | null; unit: LineItemUnit; labels?: LineItemLabel[]; shouldUpdateLabels?: boolean }) => void;
  isUnitEditable: boolean;
  loading?: boolean;
}

const BulkEditDrawLineItemsDialog: React.FC<BulkEditDrawLineItemsDialogProps> = ({
  open,
  onClose,
  onSave: onSave,
  isUnitEditable,
  loading,
}) => {
  const [selectedDivision, setSelectedDivision] = React.useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = React.useState<LineItemUnit>(null);
  const [changeOrderAction, setChangeOrderAction] = React.useState<ChangeOrderAction>('no-change');

  React.useEffect(() => {
    setSelectedDivision(null);
    setSelectedUnit(null);
    setChangeOrderAction('no-change');
  }, [open]);

  const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];
  const changeOrderOptions = [
    { value: 'no-change', label: 'No Change' },
    { value: 'mark', label: 'Mark as Change Order' },
    { value: 'unmark', label: 'Remove Change Order' },
  ];
  const canUpdate = selectedDivision !== null || selectedUnit !== null || changeOrderAction !== 'no-change';

  const handleUpdate = async () => {
    const shouldUpdateLabels = changeOrderAction !== 'no-change';
    const labels = changeOrderAction === 'mark' ? [LineItemLabelEnum.ChangeOrder] : [];

    onSave({
      divisionId: selectedDivision,
      unit: selectedUnit,
      labels,
      shouldUpdateLabels,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Edit Selected Line Items
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <SelectInput
            label='Change Order'
            selected={changeOrderAction}
            setSelected={newVal => setChangeOrderAction(newVal as ChangeOrderAction)}
            list={changeOrderOptions}
            variant='standard'
            fullWidth
          />
          <SelectDivision selectedDivision={selectedDivision} setSelectedDivision={setSelectedDivision} />
          {isUnitEditable && (
            <SelectInput
              label='Unit'
              selected={selectedUnit ?? ''}
              setSelected={newVal => {
                setSelectedUnit(newVal === '' ? null : (newVal as LineItemUnitEnum));
              }}
              list={unitsList}
              variant='standard'
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined' color='error'>
          Cancel
        </Button>
        <Button onClick={handleUpdate} disabled={!canUpdate} variant='contained' loading={loading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditDrawLineItemsDialog;
